import React, { useState, useEffect } from 'react';
import { Cloud, HardDrive, Search, User, Calendar, MapPin, ChevronDown, X, Check } from 'lucide-react';
import { useAuthStore } from '@store/authStore';
import { useKundliStore } from '@store/kundliStore';
import { kundliService, KundliProfile } from '@services/kundliService';
import { formatDob } from '@utils/dateUtils';

interface KundliSelectorProps {
    selectedKundli: any | null;
    onSelect: (kundli: any, isCloud: boolean) => void;
    onClear: () => void;
    label: string;
    language: 'english' | 'hindi';
    translations: any;
}

const KundliSelector: React.FC<KundliSelectorProps> = ({
    selectedKundli,
    onSelect,
    onClear,
    label,
    language,
    translations: t
}) => {
    const { isAuthenticated } = useAuthStore();
    const { savedKundlis } = useKundliStore();
    const [cloudKundlis, setCloudKundlis] = useState<KundliProfile[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (isAuthenticated) {
            fetchCloudKundlis();
        }
    }, [isAuthenticated]);

    const fetchCloudKundlis = async () => {
        try {
            setLoading(true);
            const response = await kundliService.getProfiles();
            setCloudKundlis(response.kundli_profiles || []);
        } catch (error) {
            console.error('Failed to fetch cloud kundlis:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredCloud = cloudKundlis.filter(k =>
        k.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    const filteredLocal = savedKundlis.filter(k =>
        k.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const hasAnyKundlis = filteredCloud.length > 0 || filteredLocal.length > 0;

    const formatDate = (dob: string) => {
        return formatDob(dob);
    };

    const getInitial = (name: string) => name?.charAt(0)?.toUpperCase() || '?';

    const handleSelect = (kundli: any, isCloud: boolean) => {
        onSelect(kundli, isCloud);
        setIsOpen(false);
        setSearchQuery('');
    };

    // If a kundli is selected, show compact selected state
    if (selectedKundli) {
        return (
            <div className="bg-gradient-to-r from-primary-600/20 to-secondary-600/20 border border-primary-500/30 rounded-xl p-3 mb-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white font-bold text-sm">
                            {getInitial(selectedKundli.name)}
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <Check size={14} className="text-green-400" />
                                <span className="text-xs text-green-400 font-medium">{t.selected}</span>
                            </div>
                            <span className="font-medium text-white text-sm">{selectedKundli.name}</span>
                            <div className="text-[11px] text-primary-200/60">
                                {formatDate(selectedKundli.dob)} • {selectedKundli.birth_location || selectedKundli.location}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClear}
                        className="p-2 rounded-lg hover:bg-white/10 text-primary-300 transition-colors"
                    >
                        <X size={16} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="mb-4 relative">
            {/* Dropdown Trigger */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-white/[0.03] border border-dashed border-white/20 rounded-xl text-primary-200/60 hover:bg-white/[0.05] hover:border-primary-500/30 transition-colors"
            >
                <div className="flex items-center gap-2">
                    <User size={16} />
                    <span className="text-sm">{t.selectSaved}</span>
                </div>
                <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Content */}
            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-cosmic-900/95 backdrop-blur-xl border border-white/20 rounded-xl overflow-hidden shadow-2xl z-50">
                    {/* Search */}
                    <div className="p-3 border-b border-white/10">
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400" />
                            <input
                                type="text"
                                placeholder={t.searchCharts}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-white/5 border border-white/20 rounded-lg py-2 pl-9 pr-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50 placeholder:text-white/40"
                                autoFocus
                            />
                        </div>
                    </div>

                    {/* List */}
                    <div className="max-h-[250px] overflow-y-auto custom-scrollbar">
                        {loading && (
                            <div className="text-center py-6 text-primary-200/50 text-sm">
                                <div className="w-5 h-5 border-2 border-primary-400/30 border-t-primary-400 rounded-full animate-spin mx-auto mb-2" />
                                Loading...
                            </div>
                        )}

                        {!loading && !hasAnyKundlis && (
                            <div className="text-center py-6 text-primary-200/50 text-sm">
                                <User size={24} className="mx-auto mb-2 opacity-30" />
                                {t.noSavedKundlis}
                                <p className="text-xs mt-1">{t.orEnterManually}</p>
                            </div>
                        )}

                        {/* Cloud Kundlis */}
                        {filteredCloud.length > 0 && (
                            <div className="p-2">
                                <div className="flex items-center gap-2 text-[10px] text-primary-300/60 font-medium uppercase tracking-wider px-2 py-1.5">
                                    <Cloud size={10} />
                                    {t.cloudSaved}
                                </div>
                                {filteredCloud.map((kundli) => (
                                    <button
                                        key={kundli.id}
                                        type="button"
                                        onClick={() => handleSelect(kundli, true)}
                                        className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/10 transition-colors text-left"
                                    >
                                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white font-bold text-xs shrink-0">
                                            {getInitial(kundli.name)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-white text-sm truncate">{kundli.name}</span>
                                                <span className="text-[10px] text-primary-300/50 uppercase">{kundli.gender?.charAt(0)}</span>
                                            </div>
                                            <div className="flex items-center gap-3 text-[10px] text-primary-200/50">
                                                <span className="flex items-center gap-1">
                                                    <Calendar size={9} />
                                                    {formatDate(kundli.dob)}
                                                </span>
                                                <span className="flex items-center gap-1 truncate">
                                                    <MapPin size={9} />
                                                    {kundli.birth_location || kundli.location}
                                                </span>
                                            </div>
                                        </div>
                                        <Cloud size={12} className="text-primary-400 shrink-0" />
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Local Kundlis */}
                        {filteredLocal.length > 0 && (
                            <div className="p-2 border-t border-white/10">
                                <div className="flex items-center gap-2 text-[10px] text-primary-300/60 font-medium uppercase tracking-wider px-2 py-1.5">
                                    <HardDrive size={10} />
                                    {t.savedLocally}
                                </div>
                                {filteredLocal.map((kundli) => (
                                    <button
                                        key={kundli.id}
                                        type="button"
                                        onClick={() => handleSelect(kundli, false)}
                                        className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/10 transition-colors text-left"
                                    >
                                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white font-bold text-xs shrink-0">
                                            {getInitial(kundli.name)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-white text-sm truncate">{kundli.name}</span>
                                                <span className="text-[10px] text-primary-300/50 uppercase">{kundli.gender?.charAt(0)}</span>
                                            </div>
                                            <div className="flex items-center gap-3 text-[10px] text-primary-200/50">
                                                <span className="flex items-center gap-1">
                                                    <Calendar size={9} />
                                                    {formatDate(kundli.dob)}
                                                </span>
                                                <span className="flex items-center gap-1 truncate">
                                                    <MapPin size={9} />
                                                    {kundli.location}
                                                </span>
                                            </div>
                                        </div>
                                        <HardDrive size={12} className="text-amber-400 shrink-0" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Close button */}
                    <div className="p-2 border-t border-white/10">
                        <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            className="w-full py-2 text-xs text-primary-300/60 hover:text-primary-300 transition-colors"
                        >
                            {t.orEnterManually}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default KundliSelector;
